import { fetchApi } from './api';
import type {
    FinancialStatementLine,
    LineAccount,
    AccountTransaction,
    FSComment,
    FSFilterOptions,
    LineConfig,
    AccountMapping,
    UnmappedAccount,
    RefreshStatus,
    FSView,
    FSViewLine,
    RenderedViewLine,
    FSReposting,
    LineReposting,
} from '../types/financialStatement';

const BASE = '/financial-statement';

export const financialStatementApi = {
    // Core statement data
    async getStatement(params: {
        company: string;
        year: number;
        month?: number;
        comparisonYear?: number;
        comparisonMonth?: number;
        lineType?: string;
        viewId?: number;
    }): Promise<{ lines: (FinancialStatementLine | RenderedViewLine)[]; filters: Record<string, unknown>; viewId?: number }> {
        const sp = new URLSearchParams();
        sp.set('company', params.company);
        sp.set('year', String(params.year));
        if (params.month) sp.set('month', String(params.month));
        if (params.comparisonYear) sp.set('comparisonYear', String(params.comparisonYear));
        if (params.comparisonMonth) sp.set('comparisonMonth', String(params.comparisonMonth));
        if (params.lineType) sp.set('lineType', params.lineType);
        if (params.viewId) sp.set('viewId', String(params.viewId));
        return fetchApi(`${BASE}?${sp}`);
    },

    async getFilters(): Promise<FSFilterOptions> {
        return fetchApi(`${BASE}/filters`);
    },

    // Drillthrough
    async getLineAccounts(params: {
        lineNr: number;
        company: string;
        year: number;
        month?: number;
        comparisonYear?: number;
    }): Promise<{ accounts: LineAccount[]; repostings: LineReposting[]; lineNr: number }> {
        const sp = new URLSearchParams();
        sp.set('company', params.company);
        sp.set('year', String(params.year));
        if (params.month) sp.set('month', String(params.month));
        if (params.comparisonYear) sp.set('comparisonYear', String(params.comparisonYear));
        return fetchApi(`${BASE}/lines/${params.lineNr}/accounts?${sp}`);
    },

    async getAccountTransactions(params: {
        accountNr: string;
        company: string;
        year: number;
    }): Promise<{ transactions: AccountTransaction[]; accountNr: string }> {
        const sp = new URLSearchParams();
        sp.set('company', params.company);
        sp.set('year', String(params.year));
        return fetchApi(`${BASE}/accounts/${params.accountNr}/transactions?${sp}`);
    },

    // Excel export (returns blob URL)
    getLineAccountsExportUrl(params: {
        lineNr: number;
        company: string;
        year: number;
        comparisonYear?: number;
    }): string {
        const sp = new URLSearchParams();
        sp.set('company', params.company);
        sp.set('year', String(params.year));
        if (params.comparisonYear) sp.set('comparisonYear', String(params.comparisonYear));
        return `/api/v1${BASE}/lines/${params.lineNr}/accounts/export?${sp}`;
    },

    getAccountTransactionsExportUrl(params: {
        accountNr: string;
        company: string;
        year: number;
    }): string {
        const sp = new URLSearchParams();
        sp.set('company', params.company);
        sp.set('year', String(params.year));
        return `/api/v1${BASE}/accounts/${params.accountNr}/transactions/export?${sp}`;
    },

    // Comments
    async getComments(params: {
        company: string;
        year: number;
        lineNr?: number;
        accountNr?: string;
        includeChildren?: boolean;
    }): Promise<{ comments: FSComment[] }> {
        const sp = new URLSearchParams();
        sp.set('company', params.company);
        sp.set('year', String(params.year));
        if (params.lineNr !== undefined) sp.set('lineNr', String(params.lineNr));
        if (params.accountNr) sp.set('accountNr', params.accountNr);
        if (params.includeChildren) sp.set('includeChildren', 'true');
        return fetchApi(`${BASE}/comments?${sp}`);
    },

    async createComment(data: {
        companyCode: string;
        year: number;
        lineNr: number;
        accountNr?: string;
        transactionRef?: string;
        commentText: string;
    }): Promise<FSComment> {
        return fetchApi(`${BASE}/comments`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    async updateComment(commentId: number, commentText: string): Promise<{ commentId: number }> {
        return fetchApi(`${BASE}/comments/${commentId}`, {
            method: 'PUT',
            body: JSON.stringify({ commentText }),
        });
    },

    async deleteComment(commentId: number): Promise<{ commentId: number; deleted: boolean }> {
        return fetchApi(`${BASE}/comments/${commentId}`, {
            method: 'DELETE',
        });
    },

    // Line configuration
    async getLinesConfig(): Promise<{ lines: LineConfig[] }> {
        return fetchApi(`${BASE}/lines`);
    },

    async createLine(data: LineConfig): Promise<LineConfig> {
        return fetchApi(`${BASE}/lines`, {
            method: 'POST',
            body: JSON.stringify({
                lineNr: data.LineNr,
                lineNameGerman: data.LineNameGerman,
                lineNameEnglish: data.LineNameEnglish,
                lineType: data.LineType,
                sumLines: data.SumLines,
            }),
        });
    },

    async updateLine(lineNr: number, data: Partial<LineConfig>): Promise<LineConfig> {
        return fetchApi(`${BASE}/lines/${lineNr}`, {
            method: 'PUT',
            body: JSON.stringify({
                lineNameGerman: data.LineNameGerman,
                lineNameEnglish: data.LineNameEnglish,
                lineType: data.LineType,
                sumLines: data.SumLines,
            }),
        });
    },

    async deleteLine(lineNr: number): Promise<{ lineNr: number; deleted: boolean }> {
        return fetchApi(`${BASE}/lines/${lineNr}`, {
            method: 'DELETE',
        });
    },

    // Account mappings
    async getAccountMappings(company: string): Promise<{ mappings: AccountMapping[]; company: string }> {
        return fetchApi(`${BASE}/account-mappings?company=${company}`);
    },

    async updateAccountMapping(accountNr: string, data: {
        companyCode: string;
        lineNrDebit: number;
        lineNrCredit: number;
    }): Promise<{ accountNr: string }> {
        return fetchApi(`${BASE}/account-mappings/${accountNr}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    async getUnmappedAccounts(company: string, year: number): Promise<{
        unmappedAccounts: UnmappedAccount[];
        count: number;
    }> {
        return fetchApi(`${BASE}/unmapped-accounts?company=${company}&year=${year}`);
    },

    // Repostings
    async getRepostings(params: { company: string; year: number }): Promise<{ repostings: FSReposting[] }> {
        const sp = new URLSearchParams();
        sp.set('company', params.company);
        sp.set('year', String(params.year));
        return fetchApi(`${BASE}/repostings?${sp}`);
    },

    async createReposting(data: {
        companyCode: string;
        year: number;
        debitLineNr: number;
        creditLineNr: number;
        amount: number;
        reason: string;
    }): Promise<FSReposting> {
        return fetchApi(`${BASE}/repostings`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    async updateReposting(repostingId: number, data: {
        debitLineNr: number;
        creditLineNr: number;
        amount: number;
        reason: string;
    }): Promise<{ repostingId: number; updated: boolean }> {
        return fetchApi(`${BASE}/repostings/${repostingId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    async deleteReposting(repostingId: number): Promise<{ repostingId: number; deleted: boolean }> {
        return fetchApi(`${BASE}/repostings/${repostingId}`, {
            method: 'DELETE',
        });
    },

    // Data refresh
    async triggerRefresh(companyCode: string): Promise<RefreshStatus> {
        return fetchApi(`${BASE}/refresh`, {
            method: 'POST',
            body: JSON.stringify({ companyCode }),
        });
    },

    async getRefreshStatus(runId: string): Promise<RefreshStatus> {
        return fetchApi(`${BASE}/refresh/status?runId=${runId}`);
    },

    // Views
    async getViews(params: { viewType?: string; company?: string }): Promise<{ views: FSView[] }> {
        const sp = new URLSearchParams();
        if (params.viewType) sp.set('viewType', params.viewType);
        if (params.company) sp.set('company', params.company);
        return fetchApi(`${BASE}/views?${sp}`);
    },

    async getViewLines(viewId: number): Promise<{ view: FSView; lines: FSViewLine[] }> {
        return fetchApi(`${BASE}/views/${viewId}`);
    },

    async createView(data: {
        viewName: string;
        viewType: string;
        companyCode?: string | null;
        isDefault?: boolean;
        lines: Array<{
            sortOrder: number;
            rowType: string;
            sourceLineNr?: number | null;
            displayNameGerman?: string | null;
            displayNameEnglish?: string | null;
            indentLevel?: number;
            isBold?: boolean;
            formulaSortOrders?: string | null;
        }>;
    }): Promise<{ viewId: number; viewName: string }> {
        return fetchApi(`${BASE}/views`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    async updateView(viewId: number, data: {
        viewName: string;
        companyCode?: string | null;
        isDefault?: boolean;
        lines: Array<{
            sortOrder: number;
            rowType: string;
            sourceLineNr?: number | null;
            displayNameGerman?: string | null;
            displayNameEnglish?: string | null;
            indentLevel?: number;
            isBold?: boolean;
            formulaSortOrders?: string | null;
        }>;
    }): Promise<{ viewId: number; viewName: string }> {
        return fetchApi(`${BASE}/views/${viewId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    async deleteView(viewId: number): Promise<{ viewId: number; deleted: boolean }> {
        return fetchApi(`${BASE}/views/${viewId}`, {
            method: 'DELETE',
        });
    },

    async duplicateView(viewId: number): Promise<{ viewId: number; viewName: string }> {
        return fetchApi(`${BASE}/views/${viewId}/duplicate`, {
            method: 'POST',
        });
    },

    async setDefaultView(viewId: number): Promise<{ viewId: number; isDefault: boolean }> {
        return fetchApi(`${BASE}/views/${viewId}/set-default`, {
            method: 'POST',
        });
    },
};
